import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Radar, KeyRound, Loader2 } from "lucide-react";
import { adminLogin, fetchAdminAuthStatus } from "@/lib/api";
import { ADMIN_LOGIN } from "@/constants/testIds/admin";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // If already authenticated, skip the form.
  useEffect(() => {
    let alive = true;
    fetchAdminAuthStatus()
      .then((r) => {
        if (alive && r?.authenticated) navigate("/admin/review", { replace: true });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      await adminLogin(token);
      navigate("/admin/review", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) setError("Invalid admin token.");
      else setError("Could not sign in. Try again.");
      setToken("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={ADMIN_LOGIN.screen}
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0b1120] px-6"
      style={{
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center border border-[#f59e0b]/40 bg-[#f59e0b]/10">
            <Radar className="h-5 w-5 text-[#f59e0b]" />
          </div>
          <div className="leading-tight">
            <div className="text-base font-bold tracking-tight text-[#f8fafc]">
              SIGNAL EDGE
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#f59e0b]">
              admin console
            </div>
          </div>
        </div>

        <div className="border border-[#1e293b] bg-[#111827] p-6">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">
            Restricted
          </div>
          <h1 className="text-xl font-bold tracking-tight text-[#f8fafc]">
            Admin sign-in
          </h1>
          <p className="mt-1 text-sm text-[#94a3b8]">
            Paste your admin token to access the Review Queue. Session lasts 8 hours.
          </p>

          <form onSubmit={onSubmit} className="mt-5 space-y-3">
            <div className="relative">
              <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748b]" />
              <input
                type="password"
                autoFocus
                autoComplete="off"
                data-testid={ADMIN_LOGIN.tokenInput}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Admin token"
                disabled={loading}
                className="w-full border border-[#1e293b] bg-[#0f172a] py-2.5 pl-10 pr-3 text-sm text-[#f8fafc] placeholder:text-[#64748b] focus:border-[#f59e0b] focus:outline-none focus:ring-1 focus:ring-[#f59e0b] disabled:opacity-60"
              />
            </div>
            {error && (
              <div
                data-testid={ADMIN_LOGIN.error}
                role="alert"
                className="border border-[#f43f5e]/30 bg-[#f43f5e]/10 px-3 py-2 text-xs text-[#f43f5e]"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading || !token}
              data-testid={ADMIN_LOGIN.submitButton}
              className="inline-flex w-full items-center justify-center gap-2 border border-[#f59e0b] bg-[#f59e0b] px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#0b1120] transition-colors hover:bg-[#fbbf24] disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Signing in…
                </>
              ) : (
                "Enter Review Queue"
              )}
            </button>
          </form>
        </div>

        <div className="mt-4 text-center text-[11px] leading-relaxed text-[#64748b]">
          <Link to="/" className="hover:text-[#94a3b8]">← Back to Signal Edge</Link>
        </div>
      </div>
    </div>
  );
}
